import { TaskBudget } from "../../bryntum-lib/models/TaskBudget.jsx";
import { NumberFormat } from "@bryntum/schedulerpro";
import { NO_VALUE, TASK_BUDGET_FIELDS } from "../constants/OtherKeys.jsx";
import { defaultGroupRenderer as groupRenderer } from "./utils.jsx";

const defaultBudgetCurrencyFormat = "$9,999.";
const defaultBudgetDecimalFormat = "0%";
export const budgetCurrencyFormat = new NumberFormat(
  defaultBudgetCurrencyFormat
);
export const budgetNumberFormat = new NumberFormat(defaultBudgetDecimalFormat);

const EXCLUDED_ANNOTATED_FIELDS = [
  TASK_BUDGET_FIELDS.laborVariance,
  TASK_BUDGET_FIELDS.materialsVariance,
  TASK_BUDGET_FIELDS.subsVariance,
  TASK_BUDGET_FIELDS.rentalsVariance,
  TASK_BUDGET_FIELDS.miscellaneousVariance,
  TASK_BUDGET_FIELDS.daysVariance,
  TASK_BUDGET_FIELDS.daysActual,
  TASK_BUDGET_FIELDS.totalCostVariance,
  TASK_BUDGET_FIELDS.grossProfitVariance,
  TASK_BUDGET_FIELDS.grossMarginVariance
]
export const annotateField = (element, widgetField, record) => {
  // If no budget is assigned to the record, then return
  // Also being triggered from task grid, so need to check if there is a updatedFields property in record
  if (!record?.updatedFields?.length) return;
  const modelField = TaskBudget.getFieldDefinition(widgetField);
  if (modelField && !EXCLUDED_ANNOTATED_FIELDS.includes(modelField.name)) {
    if (record.updatedFields.includes(modelField.dataSource)) {
      element.classList.add("budget-cell", "budget-field-change");
    } else {
      element.classList.remove("budget-cell", "budget-field-change");
    }
  }
};

export const budgetColRenderer = (context, canApplyStyle = false) => {
  let text = null;
  const { value, column } = context;
  const { defaultRenderer } = column;
  const style = {};

  if (canApplyStyle) {
    if (value < 0) {
      style.color = "#ff0000";
    } else {
      style.color = "#00b900";
    }
    text = !isNaN(value)
      ? defaultRenderer.call(column, { value })
      : null;
  } else {
    text = !isNaN(value) ? defaultRenderer.call(column, { value }) : null;
  }

  return {
    text: text || "N/A",
    style: value ? style : null
  };
};

export const budgetSummaryRenderer = (valueFormatter, text = "", canApplyStyle = false) => (
  ({ sum }) => {
    let value = sum,
      style = "";
    if (canApplyStyle) {
      if (value < 0) {
        style = "color: #ff0000";
      } else if (value > 0) {
        style = "color: #00b900";
      }
    }
    if (valueFormatter) {
      value = valueFormatter.format(
        valueFormatter.style === "percent" ? value / 100 : value
      );
    }
    return `${text} <span style="${style}">${value}</span>`;
  }
);

// This value is being used in multiple files
export const BUDGET_MODEL_KEY_PREFIX = "budgetRecord.";
export const DEFAULT_BUDGET_FIELDS = {
  amountField: {
    type: "number", // required as this config is being used in editor fields
    format: budgetCurrencyFormat,
    align: "left",
    groupRenderer: ({ groupRowFor: value, count }) => {
      return groupRenderer({
        groupRowFor: value !== null && value !== undefined && value !== NO_VALUE ? budgetCurrencyFormat.format(value) : value,
        count
      });
    },
    max: 999999999999999 // Upto 15 digits
  },
  percentageField: {
    type: "number",
    align: "left",
    groupRenderer: ({ groupRowFor: value, count }) => {
      return groupRenderer({
        groupRowFor: value !== null && value !== undefined && value !== NO_VALUE ? value + "%" : value,
        count
      });
    },
    // Format config behaves unexpectedly in percentage field, so we need to format the value
    // Using timeout since internal listeners are updating the value
    // https://forum.bryntum.com/viewtopic.php?p=158661#p158661
    onChange({ source }) {
      setTimeout(() => {
        if(source.value) {
          source.input.value = source.value + "%";
        }
      });
    },
    onFocusOut({ fromWidget: source }) {
      setTimeout(() => {
        if(source.value) {
          source.input.value = source.value + "%";
        }
      });
    }
  }
};

export const isColorBudgetCell = (field) => {
  return [
    TASK_BUDGET_FIELDS.laborVariance,
    TASK_BUDGET_FIELDS.materialsVariance,
    TASK_BUDGET_FIELDS.subsVariance,
    TASK_BUDGET_FIELDS.rentalsVariance,
    TASK_BUDGET_FIELDS.miscellaneousVariance,
    TASK_BUDGET_FIELDS.daysVariance
  ].includes(field.replace(BUDGET_MODEL_KEY_PREFIX, ""));
};

export const disabledFieldsConfig = {
  cls: "swivu-budget-grid-header-cell [&>.b-grid-header-text]:!text-[#BB6804] dark:[&>.b-grid-header-text]:!text-[#F5980C]",
  cellCls: "!text-[#BB6804] dark:!text-[#F5980C]"
}

export const isNegativeField = (field) => {
  return [
    TASK_BUDGET_FIELDS.laborVariance,
    TASK_BUDGET_FIELDS.materialsVariance,
    TASK_BUDGET_FIELDS.subsVariance,
    TASK_BUDGET_FIELDS.rentalsVariance,
    TASK_BUDGET_FIELDS.miscellaneousVariance,
    TASK_BUDGET_FIELDS.daysVariance,
    TASK_BUDGET_FIELDS.grossProfitEstimated,
    TASK_BUDGET_FIELDS.grossMarginEstimated,
    TASK_BUDGET_FIELDS.grossProfitActual,
    TASK_BUDGET_FIELDS.grossMarginActual,
    TASK_BUDGET_FIELDS.totalCostVariance,
    TASK_BUDGET_FIELDS.grossProfitVariance,
    TASK_BUDGET_FIELDS.grossMarginVariance
  ].includes(field);
}